import { PayrollDataTable } from '@/components/(payroll_table)/components/PayrollDataTable'
import { PayrollLoansPanel } from '@/components/(payroll_table)/components/payroll-loans-panel'
import { payrollTableColumns } from '@/components/(payroll_table)/components/payrollTableColumns'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { usePayrollManualEntries } from '@/hooks/use-payroll-manual-entries'
import {
  exportPayrollToExcel,
  exportPayslipToExcel,
  exportPerEmployeeTimesheetsExcel,
  exportToDisbursementExcel,
} from '@/lib/excel_gen/payroll'
import { generateStatementOfAccountExcelFile } from '@/lib/excel_gen/statement-of-account'
import { formatCurrencyInPHP, sleep } from '@/lib/utils/generic-utils'
import { useTRPC } from '@/trpc/react'
import type { AppRouter } from '@repo/trpc'
import type { inferRouterOutputs } from '@repo/trpc/trpc-server'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  Archive,
  Calculator,
  Calendar as CalendarIcon,
  CheckCheck,
  CheckCircle2,
  ChevronLeft,
  Clock,
  Download,
  Edit,
  ExternalLink,
  FileEdit,
  FileSpreadsheet,
  FileText,
  Lock,
  Play,
  RefreshCw,
  Settings,
  Snowflake,
  TrendingUp,
  Unlock,
  Users,
  XCircle,
} from 'lucide-react'
import { DateTime } from 'luxon'
import * as React from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/payroll/$payrollId')({
  component: RouteComponent,
})

// Component to display payroll warnings
interface PayrollWarning {
  type: 'missing_setting'
  settingKey: string
  message: string
  employeeName: string
  employeeId: string
  fallbackValue?: unknown
}

interface PayrollCalculation {
  warnings?: PayrollWarning[]
  grossPay?: number
  [key: string]: unknown
}

// Type definitions for timesheet data structures
type TimesheetWithEmployee = {
  timesheetId: string
  date: string
  clockIn?: Date | string | null
  clockOut?: Date | string | null
  regularHoursTime?: number | string | null
  overtimeHoursTime?: number | string | null
  nightDifferentialTime?: number | string | null
  overtimeNightDifferentialTime?: number | string | null
  tardinessTime?: number | string | null
  employee: {
    firstName: string | null
    lastName: string | null
  } | null
}

type TimesheetLite = {
  timesheetId: string
  date: string
}

const PayrollWarnings = ({ calculations }: { calculations: PayrollCalculation[] }) => {
  const allWarnings = calculations.flatMap((calc) => calc.warnings || [])

  if (allWarnings?.length === 0) return null

  // Group warnings by employee
  const warningsByEmployee: Record<string, { employeeName: string; keys: string[] }> = {}
  for (const warning of allWarnings) {
    if (!warningsByEmployee[warning.employeeId]) {
      warningsByEmployee[warning.employeeId] = {
        employeeName: warning.employeeName,
        keys: [],
      }
    }
    warningsByEmployee[warning.employeeId].keys.push(warning.settingKey)
  }

  const employeeIds = Object.keys(warningsByEmployee)

  return (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <h3 className="font-semibold text-red-800">Configuration Warnings</h3>
        <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-300">
          {allWarnings.length} warning{allWarnings.length > 1 ? 's' : ''}
        </Badge>
      </div>
      <TooltipProvider>
        <div className="flex flex-wrap gap-2">
          {employeeIds.map((employeeId) => {
            const { employeeName, keys } = warningsByEmployee[employeeId]
            return (
              <Tooltip key={employeeId}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="destructive"
                    className="bg-red-100 text-red-700 border-red-300 cursor-help text-xs px-3 py-1"
                  >
                    {employeeName} ({keys.length})
                  </Badge>
                </TooltipTrigger>
                <TooltipContent sideOffset={4} className="max-w-xs">
                  <div className="font-semibold mb-1 text-red-400">Missing settings:</div>
                  <ul className="list-disc list-inside text-xs text-red-400">
                    {keys.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </TooltipProvider>
    </div>
  )
}

function RouteComponent() {
  // TODO: filter by client (could be just a filter on the backend, nvm this for now)
  const api = useTRPC()
  const { payrollId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: payroll, isPending } = useQuery({
    ...api.payroll.getPayrollById.queryOptions({ payrollId }),
  })

  type RouterOutput = inferRouterOutputs<AppRouter>
  type PayrollCalcItem =
    RouterOutput['payroll']['getPayrollCalculations']['payrollCalculations'][number]
  const [selectedRows, setSelectedRows] = React.useState<PayrollCalcItem[]>([])
  const [forceRefresh, setForceRefresh] = React.useState(false)
  const [isRecalculateDialogOpen, setIsRecalculateDialogOpen] = React.useState(false)

  // Dialog state for editing multipliers
  const [isMultipliersDialogOpen, setIsMultipliersDialogOpen] = React.useState(false)
  const [editSalaryMultiplier, setEditSalaryMultiplier] = React.useState<string>('')
  const [editGovtMultiplier, setEditGovtMultiplier] = React.useState<string>('')
  // Stand-in in Step 10 (always defaulted to '1.00', no real column existed yet); hydrates from
  // the real payroll row like its two siblings above as of Step 16.
  const [editWithholdingTaxMultiplier, setEditWithholdingTaxMultiplier] =
    React.useState<string>('')

  // Date of payment popover state (local selection before save)
  const [isDateOfPaymentPopoverOpen, setIsDateOfPaymentPopoverOpen] = React.useState(false)
  const [editDateOfPayment, setEditDateOfPayment] = React.useState<Date | undefined>(undefined)

  // Initialize edit date of payment when popover opens
  React.useEffect(() => {
    if (isDateOfPaymentPopoverOpen && payroll) {
      setEditDateOfPayment(
        payroll.dateOfPayment ? DateTime.fromISO(payroll.dateOfPayment).toJSDate() : undefined,
      )
    }
  }, [isDateOfPaymentPopoverOpen, payroll?.dateOfPayment])

  // Initialize edit values when dialog opens
  React.useEffect(() => {
    if (isMultipliersDialogOpen && payroll) {
      setEditSalaryMultiplier(
        payroll?.monthlySalaryCutoffMultiplier !== undefined &&
          payroll?.monthlySalaryCutoffMultiplier !== null
          ? Number(payroll.monthlySalaryCutoffMultiplier).toFixed(2)
          : '0.50',
      )
      setEditGovtMultiplier(
        payroll?.governmentContributionMultiplier != null
          ? Number(payroll.governmentContributionMultiplier).toFixed(2)
          : '0.50',
      )
      setEditWithholdingTaxMultiplier(
        payroll?.withholdingTaxMultiplier != null
          ? Number(payroll.withholdingTaxMultiplier).toFixed(2)
          : '1.00',
      )
    }
  }, [isMultipliersDialogOpen, payroll])

  const { mutate: updateStatusMutation, isPending: isUpdatingStatus } = useMutation({
    ...api.payroll.updatePayrollStatus.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll status updated successfully')
        queryClient.invalidateQueries({
          queryKey: api.payroll.getPayrollById.queryKey({ payrollId }),
        })
      },
      onError: (error) => {
        toast.error(`Failed to update payroll status: ${error.message}`)
      },
    }),
  })

  const { mutate: updateDateOfPaymentMutation, isPending: isUpdatingDateOfPayment } = useMutation({
    ...api.payroll.updatePayrollDateOfPayment.mutationOptions({
      onSuccess: () => {
        toast.success('Date of payment updated successfully')
        queryClient.invalidateQueries({
          queryKey: api.payroll.getPayrollById.queryKey({ payrollId }),
        })
      },
      onError: (error) => {
        toast.error(`Failed to update date of payment: ${error.message}`)
      },
    }),
  })

  function handleSaveDateOfPayment(date: Date | null): void {
    updateDateOfPaymentMutation(
      {
        payrollId,
        dateOfPayment: date ? DateTime.fromJSDate(date).toISODate() : null,
      },
      {
        onSuccess: () => {
          setIsDateOfPaymentPopoverOpen(false)
        },
      },
    )
  }

  const { mutate: updateMultipliersMutation, isPending: isUpdatingMultipliers } = useMutation({
    ...api.payroll.updatePayrollMultipliers.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll multipliers updated successfully')
        setIsMultipliersDialogOpen(false)
        queryClient.invalidateQueries({
          queryKey: api.payroll.getPayrollById.queryKey({ payrollId }),
        })
        queryClient.invalidateQueries({
          queryKey: api.payroll.getPayrollCalculations.queryKey({ payrollId }),
        })
      },
      onError: (error) => {
        toast.error(`Failed to update payroll multipliers: ${error.message}`)
      },
    }),
  })

  // Helper function to invalidate payroll-related queries after freeze/unfreeze operations
  function invalidatePayrollQueries() {
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollById.queryKey({ payrollId }),
    })
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollCalculations.queryKey({
        payrollId,
        forceRefresh: false,
      }),
    })
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollCalculations.queryKey({ payrollId, forceRefresh: true }),
    })
  }

  const { mutate: freezePayrollCalculationsMutation, isPending: isFreezing } = useMutation({
    ...api.payroll.freezePayrollCalculations.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll calculations frozen successfully')
        invalidatePayrollQueries()
      },
      onError: (error) => {
        toast.error(`Failed to freeze payroll calculations: ${error.message}`)
      },
    }),
  })

  const { mutate: unfreezePayrollCalculationsMutation, isPending: isUnfreezing } = useMutation({
    ...api.payroll.unfreezePayrollCalculations.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll calculations unfrozen successfully')
        invalidatePayrollQueries()
      },
      onError: (error) => {
        toast.error(`Failed to unfreeze payroll calculations: ${error.message}`)
      },
    }),
  })

  function handleSaveMultipliers() {
    const salaryVal = editSalaryMultiplier === '' ? '0.5' : editSalaryMultiplier
    const govtVal = editGovtMultiplier === '' ? '0.5' : editGovtMultiplier

    // Validate ranges
    const salaryNum = parseFloat(salaryVal)
    const govtNum = parseFloat(govtVal)

    if (isNaN(salaryNum) || salaryNum < 0) {
      toast.error('Salary multiplier must be a valid number >= 0')
      return
    }

    if (isNaN(govtNum) || govtNum < 0 || govtNum > 1) {
      toast.error('Government contribution multiplier must be between 0 and 1')
      return
    }

    const withholdingTaxVal =
      editWithholdingTaxMultiplier === '' ? '1.00' : editWithholdingTaxMultiplier
    const withholdingTaxNum = parseFloat(withholdingTaxVal)
    if (isNaN(withholdingTaxNum) || withholdingTaxNum < 0) {
      toast.error('Withholding tax multiplier must be a valid number >= 0')
      return
    }

    // Stand-in in Step 10 (console-logged only); sent for real as of Step 16.
    updateMultipliersMutation({
      payrollId,
      monthlySalaryCutoffMultiplier: salaryVal,
      governmentContributionMultiplier: govtVal,
      withholdingTaxMultiplier: withholdingTaxVal,
    })
  }

  const {
    data: payrollCalculationsAndTimesheetsByEmployee,
    isPending: isPayrollCalculationsPending,
    isError: isPayrollCalculationsError,
    error: payrollCalculationsError,
    refetch: refetchPayrollCalculations,
  } = useQuery({
    ...api.payroll.getPayrollCalculations.queryOptions(
      { payrollId, forceRefresh },
      {
        retry: false,
      },
    ),
  })

  // Reset forceRefresh after query completes
  React.useEffect(() => {
    if (!isPayrollCalculationsPending && forceRefresh) {
      setForceRefresh(false)
    }
  }, [isPayrollCalculationsPending, forceRefresh])

  /** After payroll recalculation, PAD rows can change (e.g. auto-applied loans). Per-employee manual-entry queries must refresh or payslip line items stay stale. */
  function invalidateEmployeePadCachesForPayroll(
    calculations: PayrollCalcItem[] | undefined,
  ): void {
    if (!calculations || calculations.length === 0) {
      return
    }
    for (const row of calculations) {
      void queryClient.invalidateQueries({
        queryKey: api.payroll.getEmployeePayrollAdditionsDeductions.queryKey({
          employeeId: row.employeeId,
          payrollId,
        }),
      })
    }
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollManualEntries.queryKey({ payrollId }),
    })
  }

  async function handleForceRefresh(): Promise<void> {
    setForceRefresh(true)
    const result = await refetchPayrollCalculations()
    invalidateEmployeePadCachesForPayroll(result.data?.payrollCalculations)
  }

  const {
    data: holidays,
    isPending: isHolidaysPending,
    isError: isHolidaysError,
    error: holidaysError,
  } = useQuery({
    ...api.bracketHolidays.getAllHolidaysCurrentYear.queryOptions(),
  })

  const {
    data: soaGovernmentContributions,
    isPending: isSoaGovernmentContributionsPending,
    isError: isSoaGovernmentContributionsError,
    error: soaGovernmentContributionsError,
  } = useQuery({
    ...api.soaGovernmentContributions.getSoaGovernmentContributionBrackets.queryOptions(),
  })

  const {
    data: clientSettings,
    isPending: isClientSettingsPending,
    isError: isClientSettingsError,
    error: clientSettingsError,
  } = useQuery({
    ...api.clientSettings.getClientSettings.queryOptions({ clientId: payroll?.clientId ?? '' }),
    enabled: !!payroll?.clientId,
  })

  // Fetch permanent business settings (for export options like calculateRegionalHolAsSpecialHol)
  const { data: permanentBusinessSettings } = useQuery({
    ...api.permanentBusinessSettings.getPermanentBusinessSettings.queryOptions(),
  })

  // Fetch business info (initially disabled, will be refetched on export)
  const { refetch: refetchBusinessInfo } = useQuery({
    ...api.business.getMyBusiness.queryOptions(),
    enabled: false,
  })

  // Fetch business logo (initially disabled, will be refetched on export)
  const { refetch: refetchBusinessLogo } = useQuery({
    ...api.business.getOrganizationLogo.queryOptions(),
    enabled: false,
  })

  // Fetch manual entries for this payroll
  const {
    getManualEntriesSummary,
    getEmployeesWithManualEntries,
    // isLoadingManualEntries,
    // manualEntriesError,
  } = usePayrollManualEntries(payrollId)

  // console.log({ payrollCalculations })

  // if (isPayrollCalculationsError) {
  //   return (
  //     <DashboardShell>
  //       <DashboardHeader heading="Payroll" text="Payroll details" />
  //       <div className="text-red-500">
  //         Error loading payroll calculations:{' '}
  //         {payrollCalculationsError?.message ?? 'Unknown error'}
  //       </div>
  //     </DashboardShell>
  //   )
  // }

  if (isHolidaysError) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Payroll" text="Payroll details" />
        <div className="text-red-500">
          Error loading holidays: {holidaysError?.message ?? 'Unknown error'}
        </div>
      </DashboardShell>
    )
  }

  if (isSoaGovernmentContributionsError) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Payroll" text="Payroll details" />
        <div className="text-red-500">
          Error loading SOA government contributions:{' '}
          {soaGovernmentContributionsError?.message ?? 'Unknown error'}
        </div>
      </DashboardShell>
    )
  }

  if (isPayrollCalculationsError) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Payroll" text="Payroll details" />
        <div className="text-red-500">
          Error loading payroll calculations:{' '}
          {payrollCalculationsError?.message ?? 'Unknown error'}
        </div>
      </DashboardShell>
    )
  }

  if (isClientSettingsError) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Payroll" text="Payroll details" />
        <div className="text-red-500">
          Error loading client settings: {clientSettingsError?.message ?? 'Unknown error'}
        </div>
      </DashboardShell>
    )
  }

  console.log({ holidays })

  if (
    isPending ||
    isPayrollCalculationsPending ||
    isHolidaysPending ||
    isSoaGovernmentContributionsPending ||
    isClientSettingsPending
  ) {
    return (
      <DashboardShell>
        <div className="mb-2 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/dashboard/admin/payroll' })}
          >
            <ChevronLeft className="mr-1" />
            Back
          </Button>
          /<span className="text-sm ml-2 font-bold text-neutral-700">Payroll Details</span>
        </div>
        <DashboardHeader heading="Payroll" text="Payroll details" />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )
  }

  if (!payroll?.clientId) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/dashboard/admin/payroll' })}
          >
            <ChevronLeft className="mr-1" />
            Back
          </Button>
          /<span className="text-sm ml-2 font-bold text-neutral-700">Payroll Details</span>
        </div>
        <div className="text-red-500">(Invalid Payroll)No client found for this payroll</div>
      </DashboardShell>
    )
  }

  const { payrollCalculations, timesheetsByEmployee } = payrollCalculationsAndTimesheetsByEmployee
  console.log({ payrollCalculations, timesheetsByEmployee })

  function handleCalculateOrRecalculate() {
    // If calculations exist, show confirmation dialog
    if (payrollCalculations && payrollCalculations.length > 0) {
      setIsRecalculateDialogOpen(true)
    } else {
      // No calculations exist, calculate directly
      void handleForceRefresh()
    }
  }

  async function handleConfirmRecalculate() {
    setIsRecalculateDialogOpen(false)
    // Set forceRefresh to true and refetch calculations
    setForceRefresh(true)
    const recalcResult = await refetchPayrollCalculations()
    invalidateEmployeePadCachesForPayroll(recalcResult.data?.payrollCalculations)
    // Invalidate all related query caches to refresh UI, including the "last calculated" timestamp
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollCalculations.queryKey({
        payrollId,
        forceRefresh: false,
      }),
    })
    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollCalculations.queryKey({ payrollId, forceRefresh: true }),
    })

    await sleep(1000)

    void queryClient.invalidateQueries({
      queryKey: api.payroll.getPayrollById.queryKey({ payrollId }),
    })
  }

  function handleRunPayroll() {
    updateStatusMutation({
      payrollId,
      status: 'completed',
    })
  }

  // Determine if Run Payroll button should be disabled
  const isRunPayrollDisabled =
    payroll?.status !== 'approved' ||
    !payrollCalculations ||
    payrollCalculations.length === 0 ||
    isPayrollCalculationsPending ||
    isUpdatingStatus

  return (
    <DashboardShell>
      <div className="flex justify-between items-center">
        <Button
          variant="link"
          size="sm"
          onClick={() => navigate({ to: '/dashboard/admin/payroll' })}
          className="w-fit"
        >
          <ChevronLeft className="mr-1" />
          Back
        </Button>
        /<span className="text-sm ml-2 font-bold text-neutral-700">Payroll Details</span>
        <div className="flex-1 flex flex-col items-end justify-start sticky top-0 z-10 bg-white dark:bg-gray-900 pb-4 border-b border-gray-200 dark:border-gray-700">
          {/* Status Badges Group */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      {payroll?.isFrozen ? (
                        <>
                          <Snowflake className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">Frozen</span>
                        </>
                      ) : (
                        <>
                          <Unlock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-500">Not Frozen</span>
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {payroll?.isFrozen
                        ? 'Calculations are frozen and will not be automatically recalculated'
                        : 'Calculations are not frozen and can be automatically recalculated'}
                    </p>
                    {payroll?.isFrozen && payroll?.frozenAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Frozen {DateTime.fromJSDate(payroll.frozenAt).toRelative()}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Visual Separator */}
          <div className="w-full h-px bg-gray-200 dark:bg-gray-700 mb-3" />

          {/* Action Buttons Group */}
          <div className="flex gap-2">
            {/* Freeze/Unfreeze Button */}
            {payroll?.isFrozen ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  unfreezePayrollCalculationsMutation({ payrollId })
                }}
                disabled={isUnfreezing || isPayrollCalculationsPending}
              >
                <Unlock className={`mr-2 h-4 w-4 ${isUnfreezing ? 'animate-spin' : ''}`} />
                Unfreeze Calculations
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  freezePayrollCalculationsMutation({ payrollId })
                }}
                disabled={isFreezing || isPayrollCalculationsPending}
              >
                <Lock className={`mr-2 h-4 w-4 ${isFreezing ? 'animate-spin' : ''}`} />
                Freeze Calculations
              </Button>
            )}
            {/* Calculate/Recalculate Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCalculateOrRecalculate}
                      disabled={isPayrollCalculationsPending || payroll?.isFrozen === true}
                    >
                      {payrollCalculations && payrollCalculations.length > 0 ? (
                        <>
                          <RefreshCw
                            className={`mr-2 h-4 w-4 ${isPayrollCalculationsPending ? 'animate-spin' : ''}`}
                          />
                          Recalculate
                        </>
                      ) : (
                        <>
                          <Calculator
                            className={`mr-2 h-4 w-4 ${isPayrollCalculationsPending ? 'animate-spin' : ''}`}
                          />
                          Calculate
                        </>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {payroll?.isFrozen === true ? (
                    <p>Payroll is frozen. Unfreeze calculations first to recalculate.</p>
                  ) : (
                    <p>
                      {payrollCalculations && payrollCalculations.length > 0
                        ? 'Recalculate all payroll values'
                        : 'Calculate payroll values'}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Run Payroll Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleRunPayroll}
                      disabled={isRunPayrollDisabled}
                    >
                      <Play className={`mr-2 h-4 w-4 ${isUpdatingStatus ? 'animate-spin' : ''}`} />
                      Finalize(Run) Payroll
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {isRunPayrollDisabled ? (
                    <div>
                      <p>Payroll must be approved and calculations must be complete</p>
                      {payroll?.status !== 'approved' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Current status: {payroll?.status ?? 'unknown'}
                        </p>
                      )}
                      {(!payrollCalculations || payrollCalculations.length === 0) && (
                        <p className="text-xs text-gray-500 mt-1">No calculations available</p>
                      )}
                    </div>
                  ) : (
                    <p>Mark payroll as completed and finalize</p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isPayrollCalculationsPending}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    const rows =
                      (selectedRows?.length ?? 0) > 0 ? selectedRows : (payrollCalculations ?? [])
                    exportPayrollToExcel(rows, payroll ?? {}, {
                      calculateRegionalHolAsSpecialHol:
                        permanentBusinessSettings?.calculateRegionalHolAsSpecialHol ?? true,
                    })
                  }}
                  className="cursor-pointer"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Export Payroll to Excel
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => {
                    const rows =
                      (selectedRows?.length ?? 0) > 0 ? selectedRows : (payrollCalculations ?? [])
                    exportToDisbursementExcel(rows, payroll ?? {})
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Export Disbursement to Excel
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => {
                    const rows =
                      (selectedRows?.length ?? 0) > 0 ? selectedRows : (payrollCalculations ?? [])
                    generateStatementOfAccountExcelFile(
                      rows,
                      payroll,
                      soaGovernmentContributions ?? [],
                      clientSettings ?? [],
                    )
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Export Statement of Account to Excel
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => {
                    const rows =
                      (selectedRows?.length ?? 0) > 0 ? selectedRows : (payrollCalculations ?? [])
                    exportPerEmployeeTimesheetsExcel(
                      (timesheetsByEmployee ?? {}) as Record<string, TimesheetWithEmployee[]>,
                      payroll ?? {},
                      rows,
                      {
                        calculateRegionalHolAsSpecialHol:
                          permanentBusinessSettings?.calculateRegionalHolAsSpecialHol ?? true,
                      },
                    )
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Export Per Employee Timesheets to Excel
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={async () => {
                    const rows =
                      (selectedRows?.length ?? 0) > 0 ? selectedRows : (payrollCalculations ?? [])

                    // Refetch business info and logo
                    const [businessInfoResult, logoResult] = await Promise.all([
                      refetchBusinessInfo(),
                      refetchBusinessLogo(),
                    ])

                    const business = businessInfoResult.data
                    const logoUrl = logoResult.data

                    // NOTE: we just follow guest's format for all organizations
                    exportPayslipToExcel(rows, payroll ?? {}, {
                      logoUrl: logoUrl ?? undefined,
                      name: business?.name ?? undefined,
                      location: business?.location ?? undefined,
                      calculateRegionalHolAsSpecialHol:
                        permanentBusinessSettings?.calculateRegionalHolAsSpecialHol ?? true,
                    })
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Export Payslip to Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <CardContent className="px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total Employees</p>
                <p className="text-2xl font-bold text-blue-900">{payrollCalculations?.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-200">
          <CardContent className="px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Gross Pay</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const baseGrossPay = payrollCalculations?.reduce(
                      (acc, curr) => acc + ((curr.grossPay as number) ?? 0),
                      0,
                    )
                    const manualAdjustment = getManualEntriesSummary().netAdjustment
                    return formatCurrencyInPHP(baseGrossPay ?? 0 + manualAdjustment)
                  })()}
                </p>
                {getManualEntriesSummary().netAdjustment !== 0 && (
                  <p className="text-xs text-gray-500">
                    Base:{' '}
                    {formatCurrencyInPHP(
                      payrollCalculations?.reduce(
                        (acc, curr) => acc + ((curr.grossPay as number) ?? 0),
                        0,
                      ) ?? 0,
                    )}{' '}
                    + Manual: {formatCurrencyInPHP(getManualEntriesSummary().netAdjustment)}
                  </p>
                )}
              </div>
              <TrendingUp className="h-8 w-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <CardContent className="px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {/* Status and Badges Row */}
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const status = payroll?.status ?? 'draft'
                      const statusIcons = {
                        draft: <FileEdit className="h-4 w-4 text-blue-600" />,
                        approved: <CheckCircle2 className="h-4 w-4 text-blue-600" />,
                        completed: <CheckCheck className="h-4 w-4 text-blue-600" />,
                        error: <XCircle className="h-4 w-4 text-blue-600" />,
                        archived: <Archive className="h-4 w-4 text-blue-600" />,
                      }
                      return statusIcons[status as keyof typeof statusIcons] ?? statusIcons.draft
                    })()}
                    <p className="text-sm font-medium text-blue-700">Status</p>
                  </div>
                  {/* Frozen Badge */}
                  {payroll?.isFrozen && payroll?.frozenAt && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary">
                            <Snowflake className="mr-1 h-3 w-3" />
                            Frozen
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Calculations frozen{' '}
                            {DateTime.fromJSDate(payroll.frozenAt).toRelative()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {DateTime.fromJSDate(payroll.frozenAt).toFormat(
                              'MMM dd, yyyy hh:mm a',
                            )}
                          </p>
                          {payroll.frozenById && (
                            <p className="text-xs text-gray-500 mt-1">
                              Frozen by user ID: {payroll.frozenById}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {/* Cached Badge */}
                  {!payroll?.isFrozen && payroll?.calculatedAt && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary">
                            <Clock className="mr-1 h-3 w-3" />
                            Cached
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Last calculated{' '}
                            {DateTime.fromJSDate(payroll.calculatedAt).toRelative()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {DateTime.fromJSDate(payroll.calculatedAt).toFormat(
                              'MMM dd, yyyy hh:mm a',
                            )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {/* Prominent Calculation Timestamp Display */}
                {(payroll?.isFrozen && payroll?.frozenAt) ||
                (!payroll?.isFrozen && payroll?.calculatedAt) ? (
                  <div className="mt-3 p-3 bg-white/80 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      {payroll?.isFrozen ? (
                        <>
                          <Snowflake className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                              Frozen
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                              {payroll.frozenAt
                                ? DateTime.fromJSDate(payroll.frozenAt).toRelative()
                                : 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {payroll.frozenAt
                                ? DateTime.fromJSDate(payroll.frozenAt).toFormat(
                                    'MMM dd, yyyy • hh:mm a',
                                  )
                                : ''}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Clock className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                              Last Calculated
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                              {payroll.calculatedAt
                                ? DateTime.fromJSDate(payroll.calculatedAt).toRelative()
                                : 'Never'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {payroll.calculatedAt
                                ? DateTime.fromJSDate(payroll.calculatedAt).toFormat(
                                    'MMM dd, yyyy • hh:mm a',
                                  )
                                : ''}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
                <Select
                  value={payroll?.status ?? 'draft'}
                  onValueChange={(value) => {
                    updateStatusMutation({
                      payrollId,
                      status: value as 'draft' | 'approved' | 'completed' | 'error' | 'archived',
                    })
                  }}
                  disabled={isUpdatingStatus}
                >
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* <div className="text-right ml-4">
                <Progress value={75} className="w-16 h-2" />
                <p className="text-xs text-blue-700 mt-1">75% Complete</p>
              </div> */}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-200">
          <CardContent className="px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Pay Period</p>
                <p className="text-sm font-bold text-gray-900">
                  {payroll?.payPeriodStart} to {payroll?.payPeriodEnd}
                </p>
                <p className="text-xs text-gray-600">
                  {
                    // payPeriodStart and payPeriodEnd are in yyyy-mm-dd format
                    (() => {
                      // Defensive: ensure both dates exist and are valid
                      if (!payroll?.payPeriodStart || !payroll?.payPeriodEnd) return '-'
                      const start = DateTime.fromISO(payroll.payPeriodStart)
                      const end = DateTime.fromISO(payroll.payPeriodEnd)
                      // Assert both are valid
                      if (!start.isValid || !end.isValid) return '-'
                      // Add 1 to include both start and end dates in the count
                      return end.diff(start, 'days').days + 1
                    })()
                  }
                  days
                </p>
              </div>
              <CalendarIcon className="h-8 w-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manual Entries Summary */}
      {getManualEntriesSummary().entryCount > 0 && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-700" />
              Manual Adjustments Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-700">
                  {formatCurrencyInPHP(getManualEntriesSummary().totalAdditions)}
                </div>
                <div className="text-sm text-blue-600">Total Additions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {formatCurrencyInPHP(getManualEntriesSummary().totalDeductions)}
                </div>
                <div className="text-sm text-blue-500">Total Deductions</div>
              </div>
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${
                    getManualEntriesSummary().netAdjustment >= 0
                      ? 'text-blue-700'
                      : 'text-blue-400'
                  }`}
                >
                  {formatCurrencyInPHP(getManualEntriesSummary().netAdjustment)}
                </div>
                <div className="text-sm text-blue-600">Net Adjustment</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-900">
                  {getManualEntriesSummary().entryCount}
                </div>
                <div className="text-sm text-blue-700">Total Entries</div>
              </div>
            </div>

            {/* Employees with manual entries */}
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="text-sm font-medium text-blue-900 mb-2">
                Employees with Manual Entries ({getEmployeesWithManualEntries().length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {getEmployeesWithManualEntries().map((employee) => (
                  <div
                    key={employee.employeeId}
                    className="flex items-center justify-between bg-blue-100 rounded-md p-2 text-sm"
                  >
                    <span className="font-medium text-blue-900">{employee.employeeName}</span>
                    <div className="text-right">
                      <div
                        className={`font-semibold ${
                          employee.totalAdjustment >= 0 ? 'text-blue-700' : 'text-blue-400'
                        }`}
                      >
                        {formatCurrencyInPHP(employee.totalAdjustment)}
                      </div>
                      <div className="text-xs text-blue-700">
                        {employee.entryCount} {employee.entryCount === 1 ? 'entry' : 'entries'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <PayrollLoansPanel payrollId={payrollId} />

      {/* Compact Summary */}
      <Card className="bg-white/70 backdrop-blur-sm border-gray-200">
        <CardContent className="px-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Payroll Details</h2>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 font-semibold"
            >
              {payroll?.business?.name ?? '-'}
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500 mb-1">Client</p>
              {payroll?.client?.clientId && (
                <Link
                  to="/dashboard/admin/clients/$clientId"
                  params={{ clientId: payroll.client.clientId }}
                  className="ml-1 hover:underline flex items-center gap-2"
                  title="Go to client settings"
                  aria-label="Go to client settings"
                  target="_blank"
                >
                  <p className="font-semibold text-gray-900">{payroll?.client?.name ?? '-'}</p>
                  <ExternalLink className="h-4 w-4 inline-block text-gray-500" />
                </Link>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Salary Multiplier</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">
                  {payroll?.monthlySalaryCutoffMultiplier !== undefined &&
                  payroll?.monthlySalaryCutoffMultiplier !== null
                    ? Number(payroll.monthlySalaryCutoffMultiplier).toFixed(2)
                    : '-'}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsMultipliersDialogOpen(true)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Gov&apos;t Contribution Multiplier</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">
                  {payroll?.governmentContributionMultiplier != null
                    ? `${Number(payroll.governmentContributionMultiplier).toFixed(2)}`
                    : '-'}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsMultipliersDialogOpen(true)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              {/* Stand-in in Step 10 (always showed hardcoded 1.00, no real column existed yet);
                  reads the real payroll.withholdingTaxMultiplier as of Step 16. */}
              <p className="text-xs text-gray-500 mb-1">Withholding Tax Multiplier</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">
                  {payroll?.withholdingTaxMultiplier != null
                    ? Number(payroll.withholdingTaxMultiplier).toFixed(2)
                    : '-'}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsMultipliersDialogOpen(true)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Date of Payment</p>
              {payroll?.isFrozen ? (
                <p className="font-semibold text-gray-900">
                  {payroll?.dateOfPayment
                    ? DateTime.fromISO(payroll.dateOfPayment).toFormat('MMM dd, yyyy')
                    : '-'}
                </p>
              ) : (
                <Popover
                  open={isDateOfPaymentPopoverOpen}
                  onOpenChange={setIsDateOfPaymentPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="max-w-[180px] justify-start font-semibold text-gray-900"
                      disabled={isUpdatingDateOfPayment}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {payroll?.dateOfPayment
                        ? DateTime.fromISO(payroll.dateOfPayment).toFormat('MMM dd, yyyy')
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <div className="space-y-3">
                      <Calendar
                        mode="single"
                        selected={editDateOfPayment}
                        onSelect={setEditDateOfPayment}
                        initialFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSaveDateOfPayment(editDateOfPayment ?? null)}
                          disabled={isUpdatingDateOfPayment}
                        >
                          {isUpdatingDateOfPayment ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleSaveDateOfPayment(null)}
                          disabled={isUpdatingDateOfPayment}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Payslips</p>
              <p className="font-semibold text-gray-900">{payrollCalculations?.length ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Processing</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-blue-700">
                  {payroll?.status
                    ? payroll.status.charAt(0).toUpperCase() + payroll.status.slice(1)
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Multipliers Dialog */}
      {/* Recalculate Confirmation Dialog */}
      <Dialog open={isRecalculateDialogOpen} onOpenChange={setIsRecalculateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recalculate Payroll?</DialogTitle>
            <DialogDescription>
              This will recalculate all payroll values. This may take a few moments.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRecalculateDialogOpen(false)}
              disabled={isPayrollCalculationsPending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmRecalculate}
              disabled={isPayrollCalculationsPending}
            >
              {isPayrollCalculationsPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Recalculating...
                </>
              ) : (
                'Recalculate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Multipliers Dialog */}
      <Dialog open={isMultipliersDialogOpen} onOpenChange={setIsMultipliersDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payroll Multipliers</DialogTitle>
            <DialogDescription>
              Update the salary cutoff multiplier and government contribution multiplier for this
              payroll.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="salary-multiplier" className="text-sm font-medium">
                Salary Multiplier
              </label>
              <Input
                id="salary-multiplier"
                type="number"
                min={0}
                step={0.01}
                value={editSalaryMultiplier}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '' || parseFloat(val) >= 0) {
                    setEditSalaryMultiplier(val)
                  }
                }}
                placeholder="0.50"
              />
              <p className="text-xs text-muted-foreground">
                Multiplier for calculating monthly salary cutoff (0.0 to 1.0)
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="govt-multiplier" className="text-sm font-medium">
                Government Contribution Multiplier
              </label>
              <Input
                id="govt-multiplier"
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={editGovtMultiplier}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 1)) {
                    setEditGovtMultiplier(val)
                  }
                }}
                placeholder="0.50"
              />
              <p className="text-xs text-muted-foreground">
                Multiplier for government contributions (0.0 to 1.0)
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="withholding-tax-multiplier" className="text-sm font-medium">
                Withholding Tax Multiplier
              </label>
              <Input
                id="withholding-tax-multiplier"
                type="number"
                min={0}
                step={0.01}
                value={editWithholdingTaxMultiplier}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '' || parseFloat(val) >= 0) {
                    setEditWithholdingTaxMultiplier(val)
                  }
                }}
                placeholder="1.00"
              />
              <p className="text-xs text-muted-foreground">
                Adjusts withheld tax from the legally-computed amount. Only change this from 1.00
                for a specific, documented reason (e.g. a year-end adjustment) — withholding tax is
                legally mandated, and this is not a discretionary setting.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsMultipliersDialogOpen(false)}
              disabled={isUpdatingMultipliers}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveMultipliers} disabled={isUpdatingMultipliers}>
              {isUpdatingMultipliers ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compact Payroll Summary */}
      {/* <div className="rounded-lg border p-4">
        <h2 className="text-base font-bold text-gray-900 mb-3">Payroll Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-500 mb-1">Business</p>
            <p className="font-semibold">{payroll?.business?.name ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Client</p>
            <p className="font-semibold">{payroll?.client?.name ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Pay Period</p>
            <p className="font-semibold">
              {payroll?.payPeriodStart && payroll?.payPeriodEnd
                ? `${payroll.payPeriodStart} to ${payroll.payPeriodEnd}`
                : '-'}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">Salary Multiplier</p>
            <p className="font-semibold">
              {payroll?.monthlySalaryCutoffMultiplier !== undefined &&
              payroll?.monthlySalaryCutoffMultiplier !== null
                ? Number(payroll.monthlySalaryCutoffMultiplier).toFixed(2)
                : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Payslips</p>
            <p className="font-semibold">{payrollCalculations?.length ?? 0}</p>
          </div>
        </div>
      </div> */}

      <Card className="w-full p-4">
        <h2 className="text-lg font-semibold mb-2">Payroll Computations</h2>
        <PayrollWarnings calculations={payrollCalculations ?? []} />
        <PayrollDataTable
          columns={payrollTableColumns}
          data={payrollCalculations ?? []}
          monthlySalaryCutoffMultiplier={Number(payroll?.monthlySalaryCutoffMultiplier ?? 0.5)}
          payPeriodStart={payroll?.payPeriodStart ?? ''}
          payPeriodEnd={payroll?.payPeriodEnd ?? ''}
          payrollId={payrollId}
          timesheetsByEmployee={(timesheetsByEmployee ?? {}) as Record<string, TimesheetLite[]>}
          onSelectedRowsChange={(rows) => setSelectedRows(rows)}
        />
      </Card>

      {/* Modern Holidays Section */}
      <Card className="bg-white/90 backdrop-blur-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">Holidays in Pay Period</CardTitle>
          <p className="text-sm text-gray-500">
            {payroll?.payPeriodStart} - {payroll?.payPeriodEnd}
          </p>
        </CardHeader>
        <CardContent>
          {(() => {
            const filteredHolidays =
              holidays?.holidays?.filter((holiday) => {
                if (!payroll?.payPeriodStart || !payroll?.payPeriodEnd) return false
                const holidayDate = new Date(holiday.date)
                const startDate = new Date(payroll.payPeriodStart)
                const endDate = new Date(payroll.payPeriodEnd)
                return holidayDate >= startDate && holidayDate <= endDate
              }) ?? []

            return filteredHolidays.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {filteredHolidays.map((holiday) => {
                  const isRegular = holiday.categoryName === 'Regular Holiday'
                  const isSpecial = holiday.categoryName === 'Special Holiday'

                  return (
                    <Card
                      key={holiday.eventId}
                      className={`${
                        isRegular
                          ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 hover:border-blue-300'
                          : isSpecial
                            ? 'bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-200 hover:border-gray-300'
                            : 'bg-gradient-to-br from-green-50 to-green-100/50 border-green-200 hover:border-green-300'
                      } transition-all duration-300`}
                    >
                      <CardContent className="px-6">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              isRegular
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700'
                                : isSpecial
                                  ? 'bg-gradient-to-r from-gray-600 to-gray-700'
                                  : 'bg-gradient-to-r from-green-600 to-green-700'
                            }`}
                          ></div>
                          <span
                            className={`text-sm font-semibold ${
                              isRegular
                                ? 'text-blue-700'
                                : isSpecial
                                  ? 'text-gray-700'
                                  : 'text-green-700'
                            }`}
                          >
                            {holiday.categoryName}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg">{holiday.name}</h3>
                        <p className="text-sm text-gray-600">
                          {new Date(holiday.date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                        <div
                          className={`mt-3 pt-3 border-t ${
                            isRegular
                              ? 'border-blue-200'
                              : isSpecial
                                ? 'border-gray-200'
                                : 'border-green-200'
                          }`}
                        >
                          <p
                            className={`text-xs ${
                              isRegular
                                ? 'text-blue-700'
                                : isSpecial
                                  ? 'text-gray-700'
                                  : 'text-green-700'
                            }`}
                          >
                            {isRegular
                              ? 'Regular Holiday rate applies'
                              : isSpecial
                                ? 'Special Holiday rate applies'
                                : 'Standard rate applies'}
                          </p>
                          {holiday.description && (
                            <p className="text-xs text-gray-500 mt-1">{holiday.description}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <CalendarIcon className="h-12 w-12 mx-auto" />
                </div>
                <p className="text-gray-500 text-sm">No holidays found in this pay period.</p>
              </div>
            )
          })()}
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
